import React, { useState } from 'react'

import { StyleSheet, Platform, ScrollView, View, TouchableOpacity } from 'react-native'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import client, { useClient } from '@sart/client'
import { useContent } from '@sart/content'
import { prettyErrorMessage, testID } from '@sart/utilities'
import { adaptive, backgroundColors, typography, colors } from '@sart/styles'

import {
  Title,
  Space,
  Button,
  TextInputExtended,
  KeyboardAvoidingView,
  ReturnFooter,
  Label,
  Text
} from '@sart/components'

import AppConfig from '../Configuration/AppConfig'
import CameraComponent from './CameraComponent'
import * as theme from './signTheme'
import { Camera, CompanyIcon } from '@sart/icons'

export default function SignUpNewCustomerSim(props) {
  const content = useContent()

  const { navigation, route } = props

  const [scanning, setScanning] = useState(false)


  const {
    cache: {
      customerDetails,
      existingSIM
    } = {}
  } = useClient()

  const ncaErrorMessage = route.params?.ncaErrorMessage


  const formik = useFormik({
    initialValues: {
      email: customerDetails && customerDetails.email ? customerDetails.email : '',
      sim: existingSIM ? existingSIM : ''
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().trim().email().required(content.forms.required),
      // 20 digits
      // 89334140202011111119 — valid
      // 89334140202011111118 — testing Luhn check
      // 9999999999999999998 — testing regex
      // 89334140200011111113 — testing year check
      // 19 digits
      // 8933414020209999954
      sim: Yup.string().trim().required(content.alreadyHaveSim.valid)
        .test('Luhn algorithm check', content.alreadyHaveSim.validSIM,
          val => {
            if (val) {
              return luhn(val);
            } else {
              return false;
            }
          })
        .matches(/^8952140\d{4}\d{7}\d$/, content.alreadyHaveSim.checkSIM)
    }),
    onSubmit: handleSignUpAsNewCustomer,
  })

  function barCodeProcessed(data) {
    setScanning(false)
    if (data != undefined) {
      formik.setFieldValue('sim', data.value.substring(0, 19))
      formik.setFieldTouched('sim', true, false)
    }
  }

  async function handleSignUpAsNewCustomer(values, form) {
    form.setSubmitting(true)
    try {
      values.email = values.email.trim().toLowerCase()
      // console.log(values)
      client.setCache({
        existingSIM: values.sim,
        customerDetails: {
          ...customerDetails,
          email: values.email
        }
      })

      await client.signInWithAgentUser({
        username: AppConfig.agentUsername,
        password: AppConfig.agentPassword
      })

      const catalogs = await client.callApi('catalog', 'getCatalogs', [client.configuration.catalogName])
      const catalog = catalogs[0]
      const catalogId = catalog.catalogId
      // console.log('catalog id is: ' + catalogId);

      client.setCache({ catalogId })

      navigation.navigate('SignUpNewCustomerDetails', values)
    } catch (error) {
      console.log('error is: ');
      console.error(error)
      const message = prettyErrorMessage(error, 'signup', content)
      form.setFieldError('sim', message)
      form.setSubmitting(false)
    }
  }

  function luhn(simNumber) {
    // Build an array with the digits in the card number
    var digits = simNumber.split('');
    for (var i = 0; i < digits.length; i++) {
      digits[i] = parseInt(digits[i], 10);
    }
    // Run the Luhn algorithm on the array
    var sum = 0;
    var alt = false;
    for (i = digits.length - 1; i >= 0; i--) {
      if (alt) {
        digits[i] *= 2;
        if (digits[i] > 9) {
          digits[i] -= 9;
        }
      }
      sum += digits[i];
      alt = !alt;
    }
    // Check the result
    if (sum % 10 == 0) {
      return (true);
    } else {
      return (false);
    }
  }
  return (
    <View style={styles.container}>
      {!scanning &&
        <ScrollView style={styles.scrollViewContainer}>
          <KeyboardAvoidingView style={styles.content}>
            <View style={styles.logo}>
              <CompanyIcon color={colors.white} height={styles.logoStyle.height} width={200}/>
            </View>
          

            <Space height={styles.spaceHeightTwo.height} />
            <Title {...theme.title}>{content.register_new_customer.signup}</Title>

            <Space height={42} />
            <View style={{ flexDirection: 'row', height: 44, alignItems:'flex-end' }}>
              <TextInputExtended
                label={content.alreadyHaveSim.label}
                value={formik.values.sim}
                placeholder={content.alreadyHaveSim.placeholder}
                maxLength={19}
                onChangeText={formik.handleChange('sim')}
                onBlur={formik.handleBlur('sim')}
                error={formik.touched.sim && formik.errors.sim}
                showErrorAndFooter={true}
                {...theme.textInput}
                // ref={ref => {
                // this.simRef = ref;
                // }}
                wrapperStyle={{ width: '90%' }}
              // onSubmitEditing={formik.handleSubmit}
                {...testID('SignUpSimNumber')}
              />

              <TouchableOpacity style={styles.camera} onPress={() => setScanning(true)}>
                <Camera onPress={() => setScanning(true)} color={colors.black}/>
              </TouchableOpacity>
            </View>
            <Space height={24} />
            <TextInputExtended
              label={content.register_number.emailLabel}
              value={formik.values.email}
              placeholder={content.register_number.emailPlaceholder}
              keyboardType="email-address"
              autoCapitalize="none"
              {...Platform.select({
                web: { autoComplete: 'email' },
                ios: { textContentType: 'emailAddress' },
                android: { autoCompleteType: 'email' }
              })}
              onChangeText={formik.handleChange('email')}
              onBlur={formik.handleBlur('email')}
              error={formik.touched.email && formik.errors.email}
              footer={content.register_number.emailSubtext}
              {...theme.textInput}
              showErrorAndFooter={true}
              {...testID('SignUpSimEmail')}
            // ref={ref => {
            // this.emailRef = ref;
            // }}
            // returnKeyType="go"
            // onSubmitEditing={() => this.simRef.focus()}
            />

            {formik.touched.sim && formik.errors.sim &&
              <Space height={36} />
            }
            {ncaErrorMessage && ncaErrorMessage !== undefined &&
              <Text style={styles.footerStyle}>{ncaErrorMessage}</Text>
            }

            <Space height={48} />

            <Button
              title={content.common.continue}
              disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
              onPress={formik.handleSubmit}
              {...theme.button}
              titleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white }}
              disabledTitleStyle={{...typography.extrabold, fontSize: 18, color: colors.srDarkGreen + 45}}
              {...testID('SignUpSimSubmit')}
            />
            <Space height={24} />
            <ReturnFooter
              cancelStyle={styles.cancelStyle}
              title={content.common.cancel}
              onPress={() => navigation.navigate('RegistrationHome')}
            />

          </KeyboardAvoidingView>
        </ScrollView>
      }
      {scanning &&
        <CameraComponent
          barCodeProcessed={barCodeProcessed}
        />
      }
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // height: '100%'
  },
  scrollViewContainer: {
    flex: 1,
    backgroundColor: backgroundColors.srGreen,
  },
  content: {
    // flex: 1,
    ...adaptive.wrapperPadding,
    justifyContent: 'center',
    ...adaptive.logoPadding,
    // paddingTop: 24,
    ...adaptive.contentHeight
  },
  cancel: {
    justifyContent: 'center',
    alignItems: 'center',
    // padding: 24
  },
  cancelStyle: {
    color: colors.white
  },
  footerStyle: {
    color: colors.error
  },
  spaceHeightOne: {
    ...adaptive.spaceHeightRegisterScreenOne
  },
  spaceHeightTwo: {
    ...adaptive.spaceHeightRegisterScreenTwo
  },
  spaceHeightThree: {
    ...adaptive.spaceHeightRegisterScreenThree
  },
  description: {
    textAlign: 'center',
    ...typography.demi,
    color: colors.srGreen
  },
  link: {
    color: colors.clear,
    fontSize: 14,
    ...typography.book
  },
  text: {
    color: colors.srGreen,
    fontSize: 14,
    ...typography.book
  },
  camera: {
    // position: 'absolute',
    height: '100%',
		justifyContent:'center',
    paddingLeft:'2%'
  },
  logo: {
    alignSelf:"center"
  },
  logoStyle: {
    ...adaptive.guruSize
  },
})

